import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';

import coldemailer_api from '../coldemailer_api';
import '../coldemailer.css';
import '../coldemailer-styles.css';

const c_StepBrief = 0;
const c_StepAudience = 1;

const c_MinEmails = 1;
const c_MaxEmails = 10;

const c_ToneOptions = [
	{ value: 'direct and professional', label: 'Direct and professional' },
	{ value: 'warm and conversational', label: 'Warm and conversational' },
	{ value: 'blunt and to the point', label: 'Blunt and to the point' },
	{ value: 'friendly and informal', label: 'Friendly and informal' },
	{ value: 'formal and corporate', label: 'Formal and corporate' }
];

const c_EmptyBrief = {
	offering: 'AI Call Trainer — a browser-based cold-call practice tool where reps run live voice roleplays against AI prospects that push back, stall, and object like the real thing, then get scored on what they actually said.',
	problem_solved: 'New reps learn to cold call on live prospects, so the pipeline pays for their mistakes. Managers have no practice environment and no way to see who is improving, so ramp time drags and call reluctance sets in.',
	call_to_action: 'Book a 15-minute demo, or start a paid plan and run your first practice call today.',
	tone: c_ToneOptions[0].value,
	email_count: 3,
	audience: 'Small to mid-size North American telemarketing and outbound sales agencies running teams of 5 to 50 reps, mostly hiring entry-level and training in-house.',
	audience_role: 'Sales Manager, Head of Sales, or agency owner — whoever owns rep ramp time and quota attainment.',
	audience_industry: 'Telemarketing and outbound B2B sales services.',
	audience_pain_points: 'Turnover is constant, so they are always training someone new. Reps get thrown onto live lists before they can handle a brush-off, burn good leads, and quit inside 90 days. Managers listen back to recordings after the damage is done, and coaching is opinion rather than measurement.',
	extra_notes: 'Proof points: practice calls cost cents, not leads; scoring covers opener, objection handling, and close, so improvement is visible per rep and per team. Pre-empt the objections that AI roleplay sounds robotic and that reps will not use it — the voices respond in real time and sessions run 3 to 5 minutes. Avoid claiming it replaces live coaching or guaranteeing specific revenue numbers.'
};

function CreateCampaignEmailsModal({ show, on_hide, email_campaign_id, on_created }) {
	const [step, set_step] = useState(c_StepBrief);
	const [brief, set_brief] = useState(c_EmptyBrief);
	const [generating, set_generating] = useState(false);
	const [error, set_error] = useState('');

	useEffect(function() {
		if(show) {
			set_step(c_StepBrief);
			set_brief(c_EmptyBrief);
			set_generating(false);
			set_error('');
		}
	}, [show]);

	const on_field_change = (field_name) => {
		return (event) => {
			const value = event.target.value;
			set_brief(function(previous) {
				return Object.assign({}, previous, { [field_name]: value });
			});
		};
	};

	// Typed values can land outside the range, so the count is clamped rather than trusted
	const on_email_count_change = (event) => {
		const raw_value = parseInt(event.target.value, 10);

		set_brief(function(previous) {
			if(isNaN(raw_value)) {
				return Object.assign({}, previous, { email_count: '' });
			}

			var clamped = raw_value;

			if(clamped < c_MinEmails) {
				clamped = c_MinEmails;
			}
			else if(clamped > c_MaxEmails) {
				clamped = c_MaxEmails;
			}

			return Object.assign({}, previous, { email_count: clamped });
		});
	};

	const on_email_count_blur = () => {
		set_brief(function(previous) {
			if(previous.email_count === '') {
				return Object.assign({}, previous, { email_count: c_MinEmails });
			}

			return previous;
		});
	};

	const can_continue_brief = () => {
		if(!brief.offering.trim()) {
			return false;
		}

		if(!brief.problem_solved.trim()) {
			return false;
		}

		if(!brief.call_to_action.trim()) {
			return false;
		}

		if(brief.email_count === '') {
			return false;
		}

		return true;
	};

	const can_generate = () => {
		if(!brief.audience.trim()) {
			return false;
		}

		if(!brief.audience_role.trim()) {
			return false;
		}

		if(!brief.audience_industry.trim()) {
			return false;
		}

		if(!brief.audience_pain_points.trim()) {
			return false;
		}

		return true;
	};

	// Generates the sequence, saves each email, then closes so the table reloads from the server.
	// Saved one at a time so a failure part way through still leaves the earlier emails created.
	const handle_generate = async () => {
		set_generating(true);
		set_error('');

		try {
			const generated = await coldemailer_api.generate_campaign_emails({
				offering: brief.offering.trim(),
				problem_solved: brief.problem_solved.trim(),
				call_to_action: brief.call_to_action.trim(),
				tone: brief.tone,
				email_count: brief.email_count,
				audience: brief.audience.trim(),
				audience_role: brief.audience_role.trim(),
				audience_industry: brief.audience_industry.trim(),
				audience_pain_points: brief.audience_pain_points.trim(),
				extra_notes: brief.extra_notes.trim()
			});

			for(var i=0; i < generated.length; ++i) {
				const draft = generated[i];

				await coldemailer_api.create_campaign_email({
					email_campaign_id: email_campaign_id,
					name: draft.name,
					subject: draft.subject,
					message: draft.message,
					content_type: draft.content_type ? draft.content_type : 'text',
					touch_number: i + 1,
					email_index: i,
					status: 'draft'
				});
			}

			if(on_created) {
				on_created();
			}

			on_hide();
		} catch (error) {
			console.log('CreateCampaignEmailsModal.handle_generate - ERROR: ', error);

			const message = error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Failed to create the emails.';
			set_error(message);
		} finally {
			set_generating(false);
		}
	};

	const handle_hide = () => {
		if(generating) {
			return;
		}

		on_hide();
	};

	const handle_back = () => {
		set_step(c_StepBrief);
	};

	const render_step_indicator = () => {
		return (
			<p className="ce-wizard-step-label">
				Step {step + 1} of 2
			</p>
		);
	};

	const render_brief_step = () => {
		return (
			<Form>
				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">What is being offered</Form.Label>
					<Form.Control
						as="textarea"
						rows={2}
						placeholder="The product or service being sold"
						value={brief.offering}
						onChange={on_field_change('offering')}
						disabled={generating}
					/>
				</Form.Group>

				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">The problem it solves</Form.Label>
					<Form.Control
						as="textarea"
						rows={2}
						placeholder="What the recipient is struggling with today"
						value={brief.problem_solved}
						onChange={on_field_change('problem_solved')}
						disabled={generating}
					/>
				</Form.Group>

				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">Call to action</Form.Label>
					<Form.Control
						type="text"
						placeholder="What you want them to do next"
						value={brief.call_to_action}
						onChange={on_field_change('call_to_action')}
						disabled={generating}
					/>
				</Form.Group>

				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">Tone of voice</Form.Label>
					<Form.Select
						value={brief.tone}
						onChange={on_field_change('tone')}
						disabled={generating}
					>
						{c_ToneOptions.map(function(option) {
							return (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							);
						})}
					</Form.Select>
				</Form.Group>

				<Form.Group className="ce-modal-field ce-field-count">
					<Form.Label className="ce-modal-label">Number of emails</Form.Label>
					<Form.Control
						type="number"
						min={c_MinEmails}
						max={c_MaxEmails}
						step={1}
						value={brief.email_count}
						onChange={on_email_count_change}
						onBlur={on_email_count_blur}
						disabled={generating}
					/>
					<Form.Text className="ce-modal-help">
						Between {c_MinEmails} and {c_MaxEmails}. Email 1 is the first contact, the rest are follow ups.
					</Form.Text>
				</Form.Group>
			</Form>
		);
	};

	const render_audience_step = () => {
		return (
			<Form>
				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">Who the audience is</Form.Label>
					<Form.Control
						as="textarea"
						rows={2}
						placeholder="The kind of person or business being contacted"
						value={brief.audience}
						onChange={on_field_change('audience')}
						disabled={generating}
					/>
				</Form.Group>

				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">Their role</Form.Label>
					<Form.Control
						type="text"
						placeholder="Job title or position"
						value={brief.audience_role}
						onChange={on_field_change('audience_role')}
						disabled={generating}
					/>
				</Form.Group>

				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">Their industry</Form.Label>
					<Form.Control
						type="text"
						placeholder="The sector they work in"
						value={brief.audience_industry}
						onChange={on_field_change('audience_industry')}
						disabled={generating}
					/>
				</Form.Group>

				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">Their pain points</Form.Label>
					<Form.Control
						as="textarea"
						rows={3}
						placeholder="What frustrates them, in their own terms"
						value={brief.audience_pain_points}
						onChange={on_field_change('audience_pain_points')}
						disabled={generating}
					/>
				</Form.Group>

				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">Anything else</Form.Label>
					<Form.Control
						as="textarea"
						rows={2}
						placeholder="Optional. Proof points, objections to pre-empt, things to avoid saying"
						value={brief.extra_notes}
						onChange={on_field_change('extra_notes')}
						disabled={generating}
					/>
				</Form.Group>
			</Form>
		);
	};

	const render_generating = () => {
		return (
			<div className="ce-modal-loading">
				<Spinner animation="border" />
				<p className="ce-wizard-generating-text">
					Writing {brief.email_count} email{brief.email_count === 1 ? '' : 's'}. This takes a few seconds.
				</p>
			</div>
		);
	};

	const render_body = () => {
		if(generating) {
			return render_generating();
		}

		if(step === c_StepBrief) {
			return render_brief_step();
		}

		return render_audience_step();
	};

	const render_footer_buttons = () => {
		if(step === c_StepBrief) {
			return (
				<Button
					className="ce-button-create"
					onClick={function() { set_step(c_StepAudience); }}
					disabled={!can_continue_brief() || generating}
				>
					Next
				</Button>
			);
		}

		return (
			<React.Fragment>
				<Button variant="outline-secondary" onClick={handle_back} disabled={generating}>
					Back
				</Button>

				<Button
					className="ce-button-create"
					onClick={handle_generate}
					disabled={!can_generate() || generating}
				>
					{generating ? 'Generating...' : 'Generate Emails'}
				</Button>
			</React.Fragment>
		);
	};

	return (
		<Modal show={show} onHide={handle_hide} centered size="lg" dialogClassName="ce-modal-dialog">
			<Modal.Header closeButton>
				<Modal.Title>Create Campaign Emails</Modal.Title>
			</Modal.Header>

			<Modal.Body className="ce-modal-body">
				{render_step_indicator()}

				{error ? <Alert variant="danger" className="ce-modal-alert">{error}</Alert> : null}

				{render_body()}
			</Modal.Body>

			<Modal.Footer>
				<Button variant="outline-secondary" onClick={handle_hide} disabled={generating}>
					Cancel
				</Button>

				{render_footer_buttons()}
			</Modal.Footer>
		</Modal>
	);
}

export default CreateCampaignEmailsModal;
